// Referrals, Who can refer (docs/referrals-spec.md 4.5): 1 product at a time, everyone who has or had it (and
// anyone ticked for it), with what shows they've done it, their code and the tick. Ticking 1 person, ticking a
// whole intake (with Undo, as Grant in bulk has), and ticking someone else found by name or email are migration
// 76's staff writes; the list is read again after each. The page never ticks anyone on its own and never
// suggests who to tick (decision 55: Adam chooses who can refer what).
import { useEffect, useMemo, useState } from 'react'
import { Link, navigate } from '../../lib/router.jsx'
import { usePhone } from '../../lib/media.js'
import { Button, Chip, Chips, EmptyState, Field, Loading, Problem, Select, adminPath, useAdmin, useLoad } from '../ui/index.js'
import { shortName, sentenceName } from '../people/words.js'
import { REF, doneSignals, firstName, refusal } from './words.js'
import { loadCandidates, loadIntakes } from './data.js'
import { PersonSearch } from './Dialogs.jsx'

function Tick({ c, product, busy, onTick }) {
  return (
    <input type="checkbox" className="rf-tick" checked={Boolean(c.ticked)} disabled={busy}
      aria-label={REF.canRefer(c.full_name, sentenceName(product))} onChange={e => onTick(c, e.target.checked)} />
  )
}

export function WhoCanRefer({ products, focusProduct }) {
  const { api, setFlash } = useAdmin()
  const phone = usePhone()
  const product = products.find(p => p.id === focusProduct) || products[0] || null
  const pid = product?.id || null
  const candidates = useLoad(() => (pid ? loadCandidates(api, pid) : Promise.resolve([])), [api, pid])
  const intakes = useLoad(() => (pid ? loadIntakes(api, pid) : Promise.resolve([])), [api, pid])
  const [cohort, setCohort] = useState('')
  const [busy, setBusy] = useState(null)
  const [batch, setBatch] = useState(null) // { id, n, cohort }: the last intake ticked, until the page changes

  useEffect(() => { setBatch(null); setCohort('') }, [pid])
  // the intake picked is always 1 of this product's: while the last product's intakes are still showing, a new read
  // corrects it once it lands (the review of 13 Oct: switching product left the last product's intake picked)
  useEffect(() => {
    const list = intakes.data || []
    if (!list.some(c => c.id === cohort)) setCohort(list[0]?.id || '')
  }, [intakes.data, cohort])

  const name = sentenceName(product)
  const picked = (intakes.data || []).find(c => c.id === cohort) || null
  const failed = e => setFlash({ text: refusal(e), tone: 'problem' })

  async function onTick(c, ticked) {
    if (busy) return
    setBusy(`tick:${c.person_id}`)
    // the tick shows at once; the read after the write shows what the database holds (a refusal puts it back)
    candidates.setData(rows => (rows || []).map(r => (r.person_id === c.person_id ? { ...r, ticked } : r)))
    try {
      await api.set_referral_tick({ person: c.person_id, product: pid, ticked })
      const first = firstName(c)
      setFlash({ text: ticked ? REF.ticked(first, name) : REF.unticked(first, name) })
      candidates.reload()
    } catch (e) { failed(e); candidates.reload() } finally { setBusy(null) }
  }

  async function undo(b) {
    try {
      await api.undo_referral_tick_batch({ batch: b.id })
      setBatch(null)
      setFlash({ text: REF.undone })
      candidates.reload()
    } catch (e) { failed(e) }
  }

  async function tickIntake() {
    if (busy || !picked) return
    setBusy('intake')
    try {
      const answer = await api.tick_referral_intake({ product: pid, cohort: picked.id })
      const n = Number(answer?.ticked) || 0
      if (!n || !answer?.batch_id) {
        setFlash({ text: REF.intakeNone })
      } else {
        const b = { id: answer.batch_id, n, cohort: picked.name }
        setBatch(b)
        setFlash({ text: REF.intakeTicked(n), actions: <Button size="sm" onClick={() => undo(b)}>{REF.undo}</Button> })
      }
      candidates.reload()
    } catch (e) { failed(e) } finally { setBusy(null) }
  }

  async function tickSomeone(person) {
    await onTick({ person_id: person.person_id, full_name: person.full_name, first_name: person.first_name }, true)
  }

  const rows = candidates.data || []
  const already = useMemo(() => rows.filter(r => r.ticked).map(r => r.person_id), [rows])

  if (!products.length) return <div className="rf-box"><EmptyState>{REF.noOffers}</EmptyState></div>

  let list
  if (candidates.status === 'loading' && !candidates.data) list = <Loading />
  else if (!candidates.data) list = <Problem error={candidates.error} onRetry={candidates.reload} />
  else if (!rows.length) list = <div className="rf-box"><EmptyState>{REF.nobodyHas(name)}</EmptyState></div>
  else if (phone) {
    list = (
      <ul className="rf-cards" aria-label={REF.whoTable(name)}>
        {rows.map(c => (
          <li key={c.person_id} className="rf-card rf-card--who">
            <span className="rf-who-text">
              <Link to={adminPath.person(c.person_id)} className="ad-td-main">{c.full_name}</Link>
              {doneSignals(c) && <span className="rf-signals">{doneSignals(c)}</span>}
              <span className="rf-who-code">{c.code ? <code className="rf-code">{c.code}</code> : <span className="ad-td-quiet">{REF.noCode}</span>}</span>
            </span>
            <Tick c={c} product={product} busy={Boolean(busy)} onTick={onTick} />
          </li>
        ))}
      </ul>
    )
  } else {
    const style = { '--ad-cols': 'minmax(0, 1fr) minmax(0, 1.6fr) 150px 110px' }
    list = (
      <div className="ad-table rf-table" role="table" aria-label={REF.whoTable(name)}>
        <div className="ad-thead" role="rowgroup">
          <div className="ad-tr" role="row" style={style}>
            {['person', 'done', 'code', 'can'].map(k => <span key={k} role="columnheader">{REF.whoColumns[k]}</span>)}
          </div>
        </div>
        <div className="ad-tbody" role="rowgroup">
          {rows.map(c => (
            <div key={c.person_id} className="ad-tr" role="row" style={style}>
              <span className="ad-td" role="cell"><Link to={adminPath.person(c.person_id)} className="ad-td-main">{c.full_name}</Link></span>
              <span className="ad-td rf-signals" role="cell">{doneSignals(c)}</span>
              <span className="ad-td" role="cell">{c.code ? <code className="rf-code">{c.code}</code> : <span className="ad-td-quiet">{REF.noCode}</span>}</span>
              <span className="ad-td" role="cell"><Tick c={c} product={product} busy={Boolean(busy)} onTick={onTick} /></span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <>
      <p className="ad-lede rf-who-lede">{REF.whoLede}</p>
      <Chips label={REF.productChips}>
        {products.map(p => (
          <Chip key={p.id} product={p} pressed={p.id === pid} onClick={() => navigate(adminPath.referralsWho(p.id), { replace: true, scroll: false })}>{shortName(p)}</Chip>
        ))}
      </Chips>
      {(intakes.data || []).length > 0 && (
        <div className="rf-intake">
          <Field label={REF.intakeLabel} className="rf-intake-field">
            {props => <Select {...props} value={cohort} onChange={setCohort} options={intakes.data.map(c => ({ value: c.id, label: c.name }))} />}
          </Field>
          <Button busy={busy === 'intake'} disabled={!picked || Boolean(busy)} onClick={tickIntake}>
            {REF.tickIntake(picked?.name || '')}
          </Button>
        </div>
      )}
      {batch && (
        <div className="rf-undo-line" role="status">
          <span>{REF.undoLine(batch.n, batch.cohort)}</span>
          <Button size="sm" onClick={() => undo(batch)}>{REF.undo}</Button>
        </div>
      )}
      {list}
      <div className="rf-add-someone">
        <span className="rf-add-label" aria-hidden="true">{REF.addSomeone}</span>
        <PersonSearch label={REF.addSomeone} placeholder={REF.addSomeonePlaceholder} buttonLabel={REF.tick}
          buttonAria={r => `Tick ${r.full_name}`} exclude={already} busyId={busy ? busy.replace('tick:', '') : null}
          onPick={tickSomeone} />
      </div>
    </>
  )
}
